import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Wordmark } from './Login';

export default function Signup() {
  const [form, setForm] = useState({ name: '', company_name: '', company_website: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.email) { setErr('Enter a valid email.'); return; }
    if (!form.company_name.trim()) { setErr('Enter your company name.'); return; }
    if (!form.company_website.trim()) { setErr('Enter your company website.'); return; }
    setBusy(true); setErr(null);
    try {
      await api.userSignup({
        email: form.email,
        name: form.name.trim() || undefined,
        company_name: form.company_name.trim(),
        company_website: form.company_website.trim(),
      });
      setDone(true);
    } catch (e: any) {
      try {
        const m = JSON.parse((e.message as string).split(': ').slice(1).join(': ')).detail;
        setErr(m || e.message);
      } catch { setErr(e.message); }
    } finally { setBusy(false); }
  }

  if (done) {
    return (
      <AuthLayout
        title="Check your inbox"
        sub={`We sent a sign-in link to ${form.email}. Click it to access your new workspace.`}
        footer={<>Wrong address? <Link to="/signup" className="text-accent hover:underline">Try again</Link></>}
      >
        <p className="text-xs text-dim leading-relaxed">
          The link is valid for 15 minutes. Didn't get it? Check spam,
          or <Link to="/login" className="text-accent hover:underline">request a new one</Link>.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Start a Sendepot workspace"
      sub="Pick a plan during onboarding. We'll email you a sign-in link."
      footer={<>Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Name (optional)" type="text" autoComplete="name"
          value={form.name} onChange={v => set('name', v)} placeholder="Jane Doe"
        />
        <Field
          label="Company name" type="text" required autoComplete="organization"
          value={form.company_name} onChange={v => set('company_name', v)}
          placeholder="Acme Outbound"
        />
        <Field
          label="Company website" type="url" required autoComplete="url"
          value={form.company_website} onChange={v => set('company_website', v)}
          placeholder="https://acme.com"
        />
        <Field
          label="Work email" type="email" required autoComplete="email"
          value={form.email} onChange={v => set('email', v)} placeholder="you@company.com"
        />
        {err && (
          <div className="text-err text-xs flex items-start gap-1.5">
            <span className="dot dot-err mt-1" />{err}
          </div>
        )}
        <div className="text-[11px] text-dim leading-relaxed">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="text-accent hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
        </div>
        <button disabled={busy} className="btn-primary btn-lg w-full">
          {busy ? 'Creating account…' : 'Create account & email link'}
        </button>
      </form>
    </AuthLayout>
  );
}


// ── Shared auth-page layout ──────────────────────────────────────────────

export function AuthLayout({
  title, sub, children, footer,
}: { title: string; sub?: string; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="h-full relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-run/10 blur-3xl" />
        <div className="absolute inset-0 grid-lines opacity-30" />
      </div>
      <div className="h-full grid place-items-center relative px-4">
        <div className="w-full max-w-[420px] animate-slide-up">
          <Link to="/" className="flex justify-center mb-6 hover:opacity-80 transition">
            <Wordmark className="h-7" />
          </Link>
          <div className="card p-6">
            <h1 className="text-base font-semibold tracking-tight">{title}</h1>
            {sub && <p className="text-xs text-dim mt-1 mb-5">{sub}</p>}
            <div className="mt-5">{children}</div>
          </div>
          {footer && (
            <p className="text-center text-xs text-dim mt-4">{footer}</p>
          )}
        </div>
      </div>
    </div>
  );
}


export function Field({
  label, type = 'text', required = false, autoComplete, value, onChange,
  placeholder, minLength, right,
}: {
  label: string; type?: string; required?: boolean; autoComplete?: string;
  value: string; onChange: (v: string) => void; placeholder?: string;
  minLength?: number; right?: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-wider text-dim">{label}</span>
        {right}
      </div>
      <input
        className="input"
        type={type} required={required} autoComplete={autoComplete}
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} minLength={minLength}
      />
    </label>
  );
}
